import { Loader2, Save, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { NoteCard } from "../types";

type Props = {
  note: Pick<NoteCard, "id" | "content">;
  onSave: (id: string, input: { content: string }) => Promise<void> | void;
  onCancel: () => void;
};

export function NoteEditForm({ note, onSave, onCancel }: Props) {
  const [content, setContent] = useState(note.content);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setContent(note.content);
    setError("");
  }, [note.id, note.content]);

  const handleSave = async () => {
    if (!content.trim()) {
      setError("Content is required.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      await onSave(note.id, { content });
      onCancel();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Update note failed.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3">
      <div className="grid gap-2">
        <textarea
          className="min-h-40 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm leading-6 text-slate-700 outline-none focus:border-blue-400"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          placeholder="Content"
          aria-label="Note content"
        />
      </div>
      {error ? <p className="mt-2 text-xs font-bold text-red-700">{error}</p> : null}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50"
          type="button"
          onClick={onCancel}
          disabled={isSaving}
        >
          <X size={14} aria-hidden="true" />
          Cancel
        </button>
        <button
          className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || !content.trim()}
        >
          {isSaving ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
          {isSaving ? "Saving" : "Save"}
        </button>
      </div>
    </div>
  );
}
